"use client";

import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  CalendarCheck,
  Flag,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Share2,
  ShieldCheck,
  ThumbsUp,
  Ticket,
  Trash2,
  Users,
} from "lucide-react";
import { useRouter } from "next/navigation";
import {
  type ComponentType,
  type ReactNode,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import {
  deleteReview,
  type PublicReviewItem,
  type PublicReviewResponse,
  reportReview,
  toggleReviewReaction,
} from "@/actions/review";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Link from "@/components/ui/link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ReviewVerificationLevel } from "@/db/schema/review";
import { cn } from "@/lib/utils";
import { ReviewMediaGrid } from "./review-media-grid";
import { ReviewResponseForm } from "./review-response-form.client";
import { ReviewStars } from "./review-stars";
import { ReviewText } from "./review-text";

type ReviewCardProps = {
  review: PublicReviewItem;
  permalink: string;
  ownerMode?: boolean;
  allowComments?: boolean;
  /** When false, write actions become sign-in prompts instead of failing. */
  isAuthenticated?: boolean;
  className?: string;
};

const reportReasons = [
  { value: "spam", label: "Spam or bot content" },
  { value: "fake", label: "Fake or misleading" },
  { value: "harassment", label: "Harassment" },
  { value: "hate", label: "Hate or abuse" },
  { value: "privacy", label: "Private information" },
  { value: "irrelevant", label: "Not relevant" },
  { value: "conflict_of_interest", label: "Conflict of interest" },
  { value: "illegal", label: "Illegal content" },
  { value: "other", label: "Other" },
] as const;

const verificationConfig: Record<
  ReviewVerificationLevel,
  { icon: ComponentType<{ className?: string }>; className: string }
> = {
  verified_booker: {
    icon: CalendarCheck,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300",
  },
  verified_attendee: {
    icon: ShieldCheck,
    className:
      "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-400/20 dark:bg-teal-400/10 dark:text-teal-300",
  },
  verified_event_booker: {
    icon: Ticket,
    className:
      "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-300",
  },
  community_member: {
    icon: Users,
    className:
      "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-300",
  },
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function relativeDate(value: Date | number | null | undefined) {
  if (!value) return null;
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

/** Links to a user's profile when a username is known; otherwise renders inert. */
function ProfileLink({
  username,
  ariaLabel,
  className,
  children,
}: {
  username: string | null | undefined;
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
}) {
  if (!username) {
    return <span className={className}>{children}</span>;
  }
  return (
    <BackAwareLink
      href={`/profile/${username}`}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </BackAwareLink>
  );
}

function formatMonth(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "numeric",
  }).format(date);
}

export function ReviewCard({
  review,
  permalink,
  ownerMode = false,
  allowComments = true,
  isAuthenticated = true,
  className,
}: ReviewCardProps) {
  const [helpful, setHelpful] = useState(review.isHelpfulByViewer);
  const [helpfulCount, setHelpfulCount] = useState(review.helpfulCount);
  const [isHelpfulPending, startHelpfulTransition] = useTransition();
  const [commentOpen, setCommentOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const reviewerName = review.reviewer.displayName;
  const profileHref = review.reviewer.username
    ? `/profile/${review.reviewer.username}`
    : permalink;
  const createdAt = relativeDate(review.publishedAt ?? review.createdAt);
  const memberSince = formatMonth(review.reviewer.hiiveMemberSince);
  const isEdited = Boolean(review.editedAt);
  const verification =
    verificationConfig[review.verificationLevel] ??
    verificationConfig.verified_booker;
  const VerificationIcon = verification.icon;
  const signInHref = `/sign-in?next=${encodeURIComponent(permalink)}`;
  // The write page loads the viewer's existing review for update, so it doubles
  // as the edit surface for the review author.
  const editHref = review.venueSlug
    ? `/hiives/${review.hiiveSlug}/venues/${review.venueSlug}/reviews/write`
    : `/hiives/${review.hiiveSlug}/reviews/write`;
  const ownerReply = review.responses.find(
    (response) => response.responseType === "owner_reply",
  );
  const reviewerFollowUp = review.responses.find(
    (response) => response.responseType === "reviewer_follow_up",
  );
  const comments = review.responses.filter(
    (response) => response.responseType === "comment",
  );
  const showComment =
    allowComments && isAuthenticated && review.discussion.canComment;
  const ownerReplyTemplates = [
    "Thank you for taking the time to share this. We appreciate the detail and will use it to improve the experience.",
    "Thanks for the feedback. We are looking into this and will follow up with the team so future visits feel smoother.",
    "We are glad you had a strong experience with us. Thanks for recognizing the team and sharing this with the community.",
  ];

  const handleHelpful = () => {
    startHelpfulTransition(async () => {
      const previousHelpful = helpful;
      const previousCount = helpfulCount;
      setHelpful(!previousHelpful);
      setHelpfulCount(Math.max(0, previousCount + (previousHelpful ? -1 : 1)));

      const result = await toggleReviewReaction({ publicId: review.publicId });
      if (!result.ok) {
        setHelpful(previousHelpful);
        setHelpfulCount(previousCount);
        toast.error(result.message);
        return;
      }

      setHelpful(result.data.helpful);
      setHelpfulCount(result.data.helpfulCount);
    });
  };

  const handleShare = async () => {
    const url =
      typeof window === "undefined"
        ? permalink
        : new URL(permalink, window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({
          title: review.title || `${reviewerName}'s review`,
          text: review.body || undefined,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Review link copied");
    } catch {
      toast.error("Could not share this review");
    }
  };

  return (
    <article
      className={cn(
        "space-y-4 rounded-[24px] border border-border bg-surface p-4 shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-05",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <BackAwareLink
          href={profileHref}
          aria-label={`View ${reviewerName}'s profile`}
          className="shrink-0 rounded-xl transition active:scale-95"
        >
          <Avatar className="h-11 w-11 rounded-xl">
            <AvatarImage src={review.reviewer.avatarUrl ?? undefined} alt="" />
            <AvatarFallback>{initials(reviewerName) || "H"}</AvatarFallback>
          </Avatar>
        </BackAwareLink>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <BackAwareLink
              href={profileHref}
              className="truncate font-semibold text-text-primary hover:underline dark:text-white"
            >
              {reviewerName}
            </BackAwareLink>
            <ReviewStars rating={review.rating} showValue size="sm" />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-secondary dark:text-white/60">
            <Badge
              variant="outline"
              className={cn("gap-1", verification.className)}
            >
              <VerificationIcon className="h-3 w-3" />
              {review.verificationLabel}
            </Badge>
            {review.isOwnReview ? (
              <Badge
                variant="outline"
                className="border-border bg-surface-alt/60 text-text-secondary dark:border-glass-border-10 dark:bg-glass-bg-02 dark:text-white/60"
              >
                Your review
              </Badge>
            ) : null}
            {review.authorityLabel ? (
              <span>{review.authorityLabel}</span>
            ) : null}
            {memberSince ? <span>Member since {memberSince}</span> : null}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild nativeButton>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 rounded-full active:scale-95"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Review actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleShare}>
              <Share2 className="h-4 w-4" />
              Share
            </DropdownMenuItem>
            {review.isOwnReview ? (
              <>
                <DropdownMenuItem asChild>
                  <Link href={editHref}>
                    <Pencil className="h-4 w-4" />
                    Edit review
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete review
                </DropdownMenuItem>
              </>
            ) : isAuthenticated ? (
              <DropdownMenuItem onClick={() => setReportOpen(true)}>
                <Flag className="h-4 w-4" />
                Report
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem asChild>
                <Link href={signInHref}>
                  <Flag className="h-4 w-4" />
                  Sign in to report
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {review.title ? (
        <h2 className="text-lg leading-snug font-semibold text-text-primary dark:text-white">
          {review.title}
        </h2>
      ) : null}
      <ReviewText
        text={review.body}
        mentions={review.mentions}
        className="text-sm text-text-primary dark:text-white/90"
      />

      {Object.keys(review.subratings).length > 0 ? (
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          {Object.entries(review.subratings).map(([key, value]) => (
            <div
              key={key}
              className="rounded-xl border border-border bg-surface-alt/60 px-3 py-2 dark:border-glass-border-10 dark:bg-glass-bg-02"
            >
              <div className="text-xs font-medium text-text-primary capitalize dark:text-white/90">
                {key.replace(/_/g, " ")}
              </div>
              <ReviewStars rating={value} size="sm" showValue />
            </div>
          ))}
        </div>
      ) : null}

      <ReviewMediaGrid media={review.media} reviewerName={reviewerName} />

      {ownerReply || reviewerFollowUp || comments.length > 0 ? (
        <div className="space-y-3 border-t border-border/70 pt-4 dark:border-glass-border-10">
          {ownerReply ? (
            <ReviewResponseBubble
              response={ownerReply}
              label="Official reply"
              tone="official"
            />
          ) : null}

          {reviewerFollowUp ? (
            <ReviewResponseBubble
              response={reviewerFollowUp}
              label="Reviewer follow-up"
              tone="follow-up"
            />
          ) : null}

          {comments.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 text-xs text-text-secondary dark:text-white/55">
                <span>
                  Discussion {comments.length}/{review.discussion.commentLimit}
                </span>
                <span>One comment per member</span>
              </div>
              {comments.map((response) => (
                <ReviewResponseBubble
                  key={response.id}
                  response={response}
                  label="Comment"
                  tone="comment"
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {ownerMode ? (
        <div className="border-t border-border/70 pt-4 dark:border-glass-border-10">
          <ReviewResponseForm
            publicId={review.publicId}
            responseType="owner_reply"
            initialBody={
              review.responses.find(
                (response) => response.responseType === "owner_reply",
              )?.body
            }
            buttonLabel={
              review.responses.some((r) => r.responseType === "owner_reply")
                ? "Update reply"
                : "Post official reply"
            }
            placeholder="Reply as the hiive or venue owner"
            templates={ownerReplyTemplates}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-3 dark:border-glass-border-10">
        <div className="flex flex-wrap items-center gap-1">
          {isAuthenticated ? (
            <Button
              type="button"
              variant={helpful ? "secondary" : "ghost"}
              size="sm"
              className="rounded-full active:scale-95"
              disabled={isHelpfulPending}
              onClick={handleHelpful}
            >
              <ThumbsUp className={cn("h-4 w-4", helpful && "fill-current")} />
              Helpful {helpfulCount > 0 ? `(${helpfulCount})` : ""}
            </Button>
          ) : (
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="rounded-full active:scale-95"
            >
              <Link href={signInHref}>
                <ThumbsUp className="h-4 w-4" />
                Helpful {helpfulCount > 0 ? `(${helpfulCount})` : ""}
              </Link>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full active:scale-95"
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
          {showComment ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full active:scale-95"
              onClick={() => setCommentOpen((open) => !open)}
            >
              <MessageSquare className="h-4 w-4" />
              Comment
            </Button>
          ) : null}
          {review.discussion.viewerCanFollowUp ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full active:scale-95"
              onClick={() => setFollowUpOpen((open) => !open)}
            >
              <MessageSquare className="h-4 w-4" />
              Follow up
            </Button>
          ) : null}
        </div>
        <BackAwareLink
          href={permalink}
          className="text-xs text-text-secondary hover:text-text-primary hover:underline dark:text-white/55 dark:hover:text-white"
        >
          {isEdited ? "Edited · " : ""}
          {createdAt ?? "View review"}
        </BackAwareLink>
      </div>

      {commentOpen ? (
        <ReviewResponseForm
          publicId={review.publicId}
          responseType="comment"
          buttonLabel="Post comment"
          placeholder={`Add one public comment (${review.discussion.commentLimit - review.discussion.commentCount} slots left)`}
          onSubmitted={() => setCommentOpen(false)}
        />
      ) : null}

      {followUpOpen ? (
        <ReviewResponseForm
          publicId={review.publicId}
          responseType="reviewer_follow_up"
          buttonLabel="Post follow-up"
          placeholder="Add one follow-up after the official reply"
          onSubmitted={() => setFollowUpOpen(false)}
        />
      ) : null}

      <ReportReviewDialog
        publicId={review.publicId}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />

      {review.isOwnReview ? (
        <DeleteReviewDialog
          publicId={review.publicId}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      ) : null}
    </article>
  );
}

function DeleteReviewDialog({
  publicId,
  open,
  onOpenChange,
}: {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const submit = () => {
    startTransition(async () => {
      const result = await deleteReview({ publicId });

      if (!result.ok) {
        toast.error(result.message);
        return;
      }

      toast.success("Review deleted");
      onOpenChange(false);
      // Refresh both the client-cached review lists and any server-rendered
      // aggregates (rating summary, counts) that included this review.
      await queryClient.invalidateQueries({ queryKey: ["reviews"] });
      router.refresh();
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete review</DialogTitle>
          <DialogDescription>
            This removes your review, rating, and photos permanently. You can
            write a new review later if you are still eligible.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isPending}
            onClick={submit}
          >
            Delete review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewResponseBubble({
  response,
  label,
  tone,
}: {
  response: PublicReviewResponse;
  label: string;
  tone: "official" | "follow-up" | "comment";
}) {
  return (
    <div
      className={cn(
        "rounded-xl bg-surface-alt/60 px-3 py-3 dark:bg-glass-bg-02",
        tone === "official" &&
          "border border-emerald-200 bg-emerald-50/70 dark:border-emerald-400/20 dark:bg-emerald-400/10",
        tone === "follow-up" &&
          "border border-sky-200 bg-sky-50/70 dark:border-sky-400/20 dark:bg-sky-400/10",
      )}
    >
      <div className="flex items-center gap-2.5">
        <ProfileLink
          username={response.author.username}
          ariaLabel={`View ${response.author.displayName}'s profile`}
          className="shrink-0 rounded-lg transition active:scale-95"
        >
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={response.author.avatarUrl ?? undefined} alt="" />
            <AvatarFallback className="text-xs">
              {initials(response.author.displayName) || "H"}
            </AvatarFallback>
          </Avatar>
        </ProfileLink>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ProfileLink
              username={response.author.username}
              className="text-sm font-semibold text-text-primary hover:underline dark:text-white"
            >
              {response.author.displayName}
            </ProfileLink>
            <Badge
              variant="outline"
              className={cn(
                "capitalize",
                tone === "official" &&
                  "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300",
                tone === "follow-up" &&
                  "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-300",
              )}
            >
              {label}
            </Badge>
          </div>
          <span className="text-xs text-text-secondary dark:text-white/55">
            {relativeDate(response.createdAt)}
            {response.editedAt ? " · edited" : ""}
          </span>
        </div>
      </div>
      <ReviewText
        text={response.body}
        mentions={response.mentions}
        className="mt-2 text-sm text-text-primary dark:text-white/90"
      />
    </div>
  );
}

function ReportReviewDialog({
  publicId,
  open,
  onOpenChange,
}: {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [reason, setReason] =
    useState<(typeof reportReasons)[number]["value"]>("fake");
  const [details, setDetails] = useState("");
  const [isPending, startTransition] = useTransition();

  const submit = () => {
    startTransition(async () => {
      const result = await reportReview({
        publicId,
        reason,
        details: details.trim() || null,
      });

      if (!result.ok) {
        toast.error(result.message);
        return;
      }

      toast.success("Report sent");
      onOpenChange(false);
      setDetails("");
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report review</DialogTitle>
          <DialogDescription>
            Reports help moderators remove fake, unsafe, or irrelevant reviews.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select
            value={reason}
            onValueChange={(value) => setReason(value as typeof reason)}
          >
            <SelectTrigger className="w-full justify-between">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {reportReasons.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            placeholder="Add details for the moderation team"
            className="min-h-28"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" disabled={isPending} onClick={submit}>
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
